/**
 * retryImport — wrap a dynamic-import factory so it retries once
 * before giving up.
 *
 * Every doc viewer is split into its own content-hashed chunk. Two
 * things make those imports fail:
 *
 *   1. A transient network blip while fetching the chunk (flaky wifi,
 *      a CDN hiccup) — a single retry almost always fixes it.
 *   2. A stale deploy: the user's tab predates the current build, so
 *      the chunk hash it asks for no longer exists on the server. A
 *      retry won't help here — the file is genuinely gone — so on the
 *      second failure we rethrow. The rejection bubbles two ways:
 *      Vite fires `window`'s `vite:preloadError` (caught globally in
 *      App.tsx → "refresh" banner) AND React surfaces it to the
 *      nearest ErrorBoundary (DocViewerDialog's, which offers Refresh
 *      / Download original / View extracted text).
 *
 * Keeping the retry here rather than at each call site means the whole
 * viewer family — and any viewer added later — gets the same
 * resilience for free. Compose with `React.lazy`:
 * `lazy(retryImport(() => import('./Foo')))`.
 */

type ImportFactory<T> = () => Promise<T>;

/** How long to wait before the single retry. Short enough not to feel
 *  laggy, long enough to ride out a momentary network stall. */
const RETRY_DELAY_MS = 350;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Wrap a dynamic-import factory so it retries once on failure. */
export function retryImport<T>(factory: ImportFactory<T>): ImportFactory<T> {
  return () =>
    factory().catch(async (firstErr: unknown) => {
      await wait(RETRY_DELAY_MS);
      try {
        return await factory();
      } catch {
        // Both attempts failed — rethrow the *original* error so the
        // message the user/telemetry sees is the real one, not a
        // second-attempt artefact. Lets the global preloadError
        // handler + the ErrorBoundary take over.
        throw firstErr;
      }
    });
}
